import * as React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@ampm-7en-ai/ui/components/Tabs';
import { FileText } from 'lucide-react';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { ConfirmDialog } from '@ampm-7en-ai/ui/components/ConfirmDialog';
import { EmptyState } from '@ampm-7en-ai/ui/components/EmptyState';
import PlatformSettingsLayout from '@/components/settings/platform/PlatformSettingsLayoutV2';
import { LEGAL_SLUG, useLegalDocuments, useSaveLegalDocument, type LegalDocument } from '@/hooks/useLegalDocuments';
import { useToast } from '@/hooks/use-toast';
import { RichTextarea } from '@ampm-7en-ai/ui/components/RichTextarea';
import { hasRichTextContent, sanitizeRichText } from '@/lib/rich-text';

export default function LegalDocuments() {
  const query = useLegalDocuments();
  const mutation = useSaveLegalDocument();
  const { toast } = useToast();
  const [contentTab, setContentTab] = React.useState('editor');
  const [draft, setDraft] = React.useState<LegalDocument | null>(null);
  const [baseline, setBaseline] = React.useState<LegalDocument | null>(null);
  const [failure, setFailure] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [switching, setSwitching] = React.useState<LegalDocument | null>(null);
  const pending = mutation.isPending;
  const dirty = Boolean(draft && baseline && (
    draft.slug !== baseline.slug || draft.title !== baseline.title ||
    draft.content !== baseline.content || draft.is_active !== baseline.is_active
  ));
  const valid = Boolean(draft && LEGAL_SLUG.test(draft.slug) && draft.title.trim() && hasRichTextContent(draft.content));
  const preview = React.useMemo(() => sanitizeRichText(draft?.content ?? ''), [draft?.content]);

  const select = (document: LegalDocument) => {
    if (dirty) {
      setSwitching(document);
      return;
    }
    open(document);
  };

  const open = (document: LegalDocument) => {
    setDraft({ ...document });
    setBaseline({ ...document });
    setFailure(null);
  };

  const change = (fields: Partial<LegalDocument>) => setDraft((current) => current ? { ...current, ...fields } : current);

  const report = (error: unknown) => {
    console.error('Legal document operation failed:', error);
    setFailure(error instanceof Error ? error.message : 'The legal document could not be saved.');
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !valid || pending) return;
    setFailure(null);
    try {
      const saved = { ...draft, title: draft.title.trim() };
      await mutation.mutateAsync({
        method: 'PUT',
        slug: saved.slug,
        fields: { title: saved.title, content: saved.content, is_active: saved.is_active },
      });
      open(saved);
      toast({ title: 'Legal article saved', description: saved.is_active ? 'The article is published.' : 'The article is unpublished.' });
    } catch (error) {
      report(error);
    }
  };

  const remove = async () => {
    if (!draft || pending) return;
    setFailure(null);
    try {
      await mutation.mutateAsync({ method: 'DELETE', slug: draft.slug });
      setDraft(null);
      setBaseline(null);
      setDeleting(false);
      toast({ title: 'Legal article deleted' });
    } catch (error) {
      setDeleting(false);
      report(error);
    }
  };

  React.useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  return (
    <div className="font-ui text-ui-fg">
      <PlatformSettingsLayout
        title="Legal articles"
        description="Manage terms and conditions, privacy policies, and other legal documents."
      >
        {query.isError && (
          <Callout tone="danger" title="Could not load legal articles" className="mb-4">
            <p>{query.error.message}</p>
            <Button variant="secondary" onClick={() => void query.refetch()} disabled={query.isFetching}>Try again</Button>
          </Callout>
        )}
        {failure && <Callout tone="danger" title="The operation could not be completed" className="mb-4">{failure}</Callout>}
        <div className="grid items-start gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <Panel padding="md">
            <h2 className="mb-3 text-ui-base font-medium">Articles</h2>
            {query.isPending ? <p role="status">Loading articles...</p> : query.data?.length === 0 ? (
              <p className="text-ui-sm text-ui-fg-muted">No legal articles available.</p>
            ) : (
              <nav aria-label="Legal articles" className="space-y-2">
                {query.data?.map((document) => (
                  <button
                    key={document.slug}
                    type="button"
                    disabled={pending}
                    aria-pressed={draft?.slug === document.slug}
                    aria-label={`${document.title} ${document.slug} ${document.is_active ? 'Published' : 'Unpublished'}`}
                    onClick={() => select(document)}
                    className="w-full rounded-ui-sm border border-ui-border p-3 text-left hover:bg-ui-subtle aria-pressed:bg-ui-subtle disabled:opacity-50"
                  >
                    <span className="block text-ui-sm font-medium">{document.title}</span>
                    <span className="my-1 block break-all text-ui-xs text-ui-fg-muted">{document.slug}</span>
                    <Pill tone={document.is_active ? 'success' : 'neutral'}>{document.is_active ? 'Published' : 'Unpublished'}</Pill>
                  </button>
                ))}
              </nav>
            )}
          </Panel>
          {!draft ? (
            <Panel><EmptyState icon={FileText} title="Select a legal article" description="Choose an existing article from the list to edit." /></Panel>
          ) : (
            <form onSubmit={(event) => void save(event)} className="min-w-0 space-y-4">
              <Panel padding="md">
                <fieldset disabled={pending} className="min-w-0 space-y-4">
                  <h2 className="text-ui-base font-medium">Edit article</h2>
                  <Field label="Slug" htmlFor="legal-slug" hint="The article slug cannot be changed.">
                    <Input id="legal-slug" value={draft.slug} disabled />
                  </Field>
                  <Field label="Title" htmlFor="legal-title">
                    <Input id="legal-title" value={draft.title} onChange={(event) => change({ title: event.target.value })} required />
                  </Field>
                  <Tabs value={contentTab} onValueChange={setContentTab}>
                    <TabsList aria-label="Article content view" appearance="pills">
                      {['editor', 'preview'].map((tab) => (
                        <TabsTrigger key={tab} value={tab} type="button">
                          {tab === 'editor' ? 'Editor' : 'Preview'}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    <TabsContent value="editor" forceMount hidden={contentTab !== 'editor'}>
                      <RichTextarea key={draft.slug} value={draft.content}
                        id="legal-content" aria-label="Article content" rows={14}
                        onChange={(content) => change({ content })} disabled={pending} />
                      <p className="mt-2 text-ui-xs text-ui-fg-muted">Select text to format it or add a link. Editing supports bold, italic, underline, lists, and links; other formatting is removed when content changes.</p>
                    </TabsContent>
                    <TabsContent value="preview">
                      <div aria-label="Article preview" className="prose min-h-[320px] max-w-none break-words rounded-ui-sm border border-ui-border p-4 dark:prose-invert"
                        dangerouslySetInnerHTML={{ __html: preview }} />
                    </TabsContent>
                  </Tabs>
                  <ToggleRow label="Published" description="Published articles are available to readers. Save to apply changes." checked={draft.is_active} onCheckedChange={(is_active) => change({ is_active })} />
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="submit" disabled={!valid || !dirty || query.isError}>{pending ? 'Working...' : 'Save article'}</Button>
                    {dirty && <span role="status" className="text-ui-xs text-ui-fg-muted">Unsaved changes</span>}
                    <Button variant="ghost" className="ml-auto" onClick={() => setDeleting(true)}>Delete article</Button>
                  </div>
                </fieldset>
              </Panel>
            </form>
          )}
        </div>
        <ConfirmDialog
          open={deleting}
          onOpenChange={setDeleting}
          title="Delete this legal article?"
          description={`This permanently deletes "${draft?.title ?? ''}" and any unsaved changes. Articles cannot be recreated from this page.`}
          tone="danger"
          confirmLabel="Delete permanently"
          pending={pending}
          onConfirm={() => void remove()}
        />
        <ConfirmDialog
          open={Boolean(switching)}
          onOpenChange={(value) => { if (!value) setSwitching(null); }}
          title="Discard unsaved changes?"
          description="Your unsaved edits will be lost when you switch articles."
          confirmLabel="Discard changes"
          tone="danger"
          onConfirm={() => {
            if (switching) open(switching);
            setSwitching(null);
          }}
        />
      </PlatformSettingsLayout>
    </div>
  );
}
